import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Star } from "lucide-react";

export const Hero = () => {
  const { t } = useTranslation();
  return (
    <section id="hero" data-testid="hero-section"
      className="relative min-h-screen bg-zinc-950 overflow-hidden pt-24 pb-12 slash-bottom">
      <div className="absolute inset-0 opacity-40" style={{
        backgroundImage: "url(https://static.prod-images.emergentagent.com/jobs/47d8df2c-1cc3-4842-9f62-a771579034aa/images/248a406dc0e20046afa753692a4105c49eab2d341b27424d3f33e70670445b83.png)",
        backgroundSize: "cover", backgroundPosition: "center",
      }} />
      <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/70 to-transparent" />
      <div className="absolute inset-0 halftone-bg opacity-50" />
      <div className="absolute top-32 right-0 w-1/2 h-12 diagonal-stripes opacity-90 hidden md:block" />

      {/* Decorative brand watermark */}
      <img
        src="/brand/logo-512.png"
        alt=""
        aria-hidden="true"
        className="hidden md:block absolute -bottom-16 -left-12 w-[420px] opacity-[0.06] pointer-events-none select-none z-0"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8 items-center min-h-[80vh]">
        <div className="lg:col-span-7 space-y-8">
          <div className="inline-flex items-center gap-2 bg-yellow-400 text-black px-4 py-2 skew-btn">
            <span className="flex items-center gap-2">
              <Zap className="w-4 h-4" strokeWidth={3} />
              <span className="font-heading uppercase tracking-widest text-sm">{t("hero.drop")}</span>
            </span>
          </div>

          <h1 data-testid="hero-title" className="font-heading text-6xl sm:text-7xl lg:text-8xl xl:text-9xl uppercase leading-[0.85] tracking-tighter text-white">
            {t("hero.line1")}{" "}<br />
            <span className="text-yellow-400">{t("hero.line2")}</span>{" "}<br />
            <span className="text-white">{t("hero.line3")}</span>
          </h1>

          <p className="text-zinc-300 text-lg sm:text-xl max-w-xl leading-relaxed">{t("hero.subtitle")}</p>

          <div className="flex flex-wrap gap-4 items-center">
            <Link to="/shop" data-testid="hero-cta-shop"
              className="skew-btn bg-yellow-400 text-black font-heading uppercase text-xl px-8 py-4 border-2 border-yellow-400 hover:bg-black hover:text-yellow-400 transition inline-flex items-center gap-3">
              <span>{t("hero.cta_shop")} <ArrowRight className="w-5 h-5 inline ml-1" strokeWidth={3} /></span>
            </Link>
            <a href="#bestsellers" data-testid="hero-cta-bestsellers"
              className="skew-btn border-2 border-white text-white font-heading uppercase text-xl px-8 py-4 hover:bg-white hover:text-black transition">
              <span>{t("hero.cta_bestsellers")}</span>
            </a>
          </div>

          <div className="flex flex-wrap gap-8 pt-6 border-t-2 border-zinc-800">
            {[
              { n: "10K+", l: t("hero.stats_happy") },
              { n: "150+", l: t("hero.stats_drops") },
              { n: "24/7", l: t("hero.stats_shipping") },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-heading text-3xl text-yellow-400">{s.n}</div>
                <div className="text-xs uppercase tracking-widest text-zinc-500">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <div className="relative">
            <div className="absolute -top-8 -left-8 bg-yellow-400 text-black p-4 brutal-shadow-black z-20 max-w-xs rotate-[-3deg]">
              <div className="font-heading text-2xl uppercase leading-none">{t("hero.quote")}</div>
              <div className="text-xs uppercase tracking-widest mt-2">{t("hero.chapter")}</div>
            </div>
            <div className="absolute -bottom-6 -right-4 bg-black border-2 border-yellow-400 text-yellow-400 p-4 z-20 max-w-[200px] rotate-[2deg]">
              <Star className="w-5 h-5 mb-2" strokeWidth={3} />
              <div className="font-heading uppercase text-lg leading-none">{t("hero.limited_incoming")}</div>
            </div>
            <div className="relative h-[480px] border-4 border-yellow-400 overflow-hidden brutal-shadow">
              <img src="https://static.prod-images.emergentagent.com/jobs/47d8df2c-1cc3-4842-9f62-a771579034aa/images/3b6845fa322f424af267eca35f60d93c6e10415cae6506816f98f3017ef0c9c5.png"
                alt="Featured" className="w-full h-full object-cover grayscale-[20%]" />
              <div className="absolute bottom-0 left-0 right-0 bg-zinc-950/90 p-4 border-t-2 border-yellow-400">
                <div className="font-jp text-yellow-400 text-xs tracking-widest">特集</div>
                <div className="font-heading text-2xl text-white uppercase">Great Sword Protag</div>
                <div className="text-yellow-400 font-bold">€ 149,99</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-yellow-400 py-3 overflow-hidden border-t-4 border-black">
        <div className="flex marquee whitespace-nowrap">
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="font-heading uppercase text-black text-xl px-8 flex items-center gap-8">
              {t("hero.marquee")}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
